// Documentation: https://github.com/zeit/next.js/blob/canary/examples/with-scroll-restoration/lib/scroll-restoration.ts

import { useCallback, useEffect, useRef } from 'react';

type ScrollToBaseOptions = Pick<ScrollToOptions, 'behavior'> & {
    offset?: number;
    delay?: number;
};

type ScrollToSectionOptions = ScrollToBaseOptions & {
    sectionRef: Element | null;
};

const isBrowser = typeof window !== 'undefined';

export const useScrollTo = () => {
    const timeoutIdRef = useRef<number | null>(null);

    const clearTimeoutIfNeeded = useCallback(() => {
        if (timeoutIdRef.current !== null) {
            clearTimeout(timeoutIdRef.current);
            timeoutIdRef.current = null;
        }
    }, []);

    useEffect(() => {
        return () => {
            clearTimeoutIfNeeded();
        };
    }, [clearTimeoutIfNeeded]);

    const scheduleScroll = useCallback(
        (scrollFn: () => void, delay: number) => {
            if (!delay) {
                scrollFn();
                return;
            }

            clearTimeoutIfNeeded();

            timeoutIdRef.current = window.setTimeout(() => {
                scrollFn();
                timeoutIdRef.current = null;
            }, delay);
        },
        [clearTimeoutIfNeeded],
    );

    const scrollToTop = useCallback(
        ({ offset = 0, behavior = 'smooth', delay = 0 }: ScrollToBaseOptions = {}) => {
            if (!isBrowser) return;

            scheduleScroll(() => {
                window.scrollTo({ top: offset, behavior });
            }, delay);
        },
        [scheduleScroll],
    );

    const scrollToSection = useCallback(
        ({ sectionRef, offset = 20, behavior = 'smooth', delay = 0 }: ScrollToSectionOptions) => {
            if (!isBrowser || !sectionRef) return;

            scheduleScroll(() => {
                const sectionTopPosition = sectionRef.getBoundingClientRect().top;
                const sectionOffset = sectionTopPosition + window.scrollY - offset;

                window.scrollTo({ top: sectionOffset, behavior });
            }, delay);
        },
        [scheduleScroll],
    );

    return { scrollToTop, scrollToSection } as const;
};

export default useScrollTo;
